import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useEffect, useRef, useState } from 'react';

export interface StickyProps {
  children: React.ReactNode;
  className?: string;
  onViewportChange?: (isInViewport: boolean) => void;
}

export const StickyInsideParent = ({
  children,
  className,
  onViewportChange,
  ...props
}: StickyProps) => {
  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(true);
    }
  }, [onViewportChange]);

  return (
    <div className={`sticky top-0 z-10 ${className}`} {...props}>
      {children}
    </div>
  );
};

/**
 * Height of the visible sticky content, published so anything that has to sit on top of it
 * (the scroll-to-top button) can offset itself in CSS instead of being mutated from here.
 *
 * Deliberately excludes the wrapper's safe-area padding: consumers add
 * `--spacing-safe-b` to their own offset, so counting it here too would lift them by one
 * extra inset whenever a Buy-Box is on screen.
 */
export const STICKY_BOTTOM_HEIGHT_PROPERTY = '--sticky-bottom-height';

export const Sticky = ({ children, onViewportChange, className, ...props }: StickyProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(false);
    }
  }, [onViewportChange]);

  useEffect(() => {
    const { style } = document.documentElement;

    style.setProperty(STICKY_BOTTOM_HEIGHT_PROPERTY, `${contentRef.current?.offsetHeight ?? 0}px`);

    return () => {
      style.removeProperty(STICKY_BOTTOM_HEIGHT_PROPERTY);
    };
    // our Buybox height is dynamic when inside/outside the viewport
  }, [contentRef?.current?.offsetHeight]);

  return (
    // pb-safe-b keeps the content clear of the iOS home indicator and the app's translucent
    // tab bar. `fixed` resolves against the visual viewport, which spans the full webview
    // frame, so unlike the in-flow sticky variants this one needs the inset itself (APPS-6514).
    <div className={`pb-safe-b fixed bottom-0 left-0 z-10 block w-full ${className}`} {...props}>
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

export const StickyOutsideViewport = ({ children, className, onViewportChange }: StickyProps) => {
  const onSiteElementRef = useRef<HTMLDivElement>(null);
  // Debounce mechanism to prevent flickering (see ORDER-3788 for details)
  const [isStableIntersecting, setIsStableIntersecting] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isIntersecting } = useIntersectionObserver(onSiteElementRef, {
    threshold: 0.1,
    intersectOnce: false,
  });

  useEffect(() => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only update the stable state after a delay to prevent flickering
    timeoutRef.current = setTimeout(() => {
      setIsStableIntersecting(isIntersecting);
    }, 150);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isIntersecting]);

  useEffect(() => {
    if (onViewportChange) {
      onViewportChange(isStableIntersecting);
    }
  }, [isStableIntersecting, onViewportChange]);

  return (
    <>
      <div ref={onSiteElementRef} className={className}>
        {children}
      </div>
      {!isStableIntersecting && <Sticky className={className}>{children}</Sticky>}
    </>
  );
};
